/**
 * Metro 2 Field Mapping Summary Component
 * 
 * Shows Metro 2 field codes and mappings applicable to the dispute:
 * - BS-1 through BS-29 field codes
 * - Field descriptions
 * - Expected values
 * - Common conflicts
 */

import React from 'react';
import {
  Code,
  ChevronDown,
  ChevronUp,
  Info,
  AlertTriangle,
  FileCode,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

// Field group colors
const FIELD_GROUP_COLORS = {
  identification: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-700',
  status: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700',
  balance: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-700',
  dates: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-700',
  payment_history: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-700',
  other: 'bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-300 border-stone-200 dark:border-stone-700',
};

// Default Metro 2 field mappings
const DEFAULT_FIELD_MAPPINGS = [
  // Identification Fields
  {
    code: 'BS-1',
    name: 'Furnisher Name',
    group: 'identification',
    description: 'Name of the data furnisher (creditor or servicer)',
    common_conflicts: ['Name changed after transfer', 'Collector vs Original Creditor'],
  },
  {
    code: 'BS-2',
    name: 'Account Number',
    group: 'identification',
    description: 'Account identification number',
    common_conflicts: ['Partial masking differences', 'Multiple account numbers'],
  },
  // Status Fields
  {
    code: 'BS-5',
    name: 'Account Type',
    group: 'status',
    description: 'Type of account (revolving, installment, mortgage, etc.)',
    common_conflicts: ['Incorrect classification', 'Changed after transfer'],
  },
  {
    code: 'BS-6',
    name: 'ECOA Code',
    group: 'status',
    description: 'Relationship to account (individual, joint, authorized user, etc.)',
    common_conflicts: ['Incorrect responsibility code', 'Joint vs Individual'],
  },
  {
    code: 'BS-17',
    name: 'Payment History Profile',
    group: 'payment_history',
    description: '24-month payment history grid showing payment status each month',
    common_conflicts: ['Chronology jumps', 'OK to late without progression', 'CO pattern after sold'],
  },
  {
    code: 'BS-17A',
    name: 'Account Status',
    group: 'status',
    description: 'Current status of account (open, closed, paid, charge-off, collection, etc.)',
    common_conflicts: ['Status vs Balance conflict', 'Closed with balance', 'Collection status'],
  },
  {
    code: 'BS-17B',
    name: 'Payment Status',
    group: 'status',
    description: 'Current payment status (current, 30-60-90-120+ days late, etc.)',
    common_conflicts: ['Status vs History conflict', 'Late 120 on collection', 'OK while derogatory'],
  },
  {
    code: 'BS-19',
    name: 'Special Comment',
    group: 'status',
    description: 'Special comment codes (disputed, settled, etc.)',
    common_conflicts: ['Missing dispute notation', 'Incorrect settlement status'],
  },
  // Balance Fields
  {
    code: 'BS-21',
    name: 'Current Balance',
    group: 'balance',
    description: 'Current balance owed on the account',
    common_conflicts: ['Balance after paid/settled', 'Balance on sold account', 'Bureau differences'],
  },
  {
    code: 'BS-22',
    name: 'Amount Past Due',
    group: 'balance',
    description: 'Amount currently past due',
    common_conflicts: ['Past due equals full balance', 'Past due with OK status', '$0 with late status'],
  },
  {
    code: 'BS-8',
    name: 'Credit Limit',
    group: 'balance',
    description: 'Credit limit or high credit amount',
    common_conflicts: ['Revolving vs installment reporting', 'Bureau differences'],
  },
  // Date Fields
  {
    code: 'BS-3',
    name: 'Date Opened',
    group: 'dates',
    description: 'Date account was opened',
    common_conflicts: ['Collection opened date vs original', 'Re-aging concerns'],
  },
  {
    code: 'BS-25',
    name: 'Date of First Delinquency',
    group: 'dates',
    description: 'Date of first delinquency leading to current status',
    common_conflicts: ['Missing DOFD', 'Inconsistent across bureaus', 'Re-aging'],
  },
  {
    code: 'BS-26',
    name: 'Date Closed',
    group: 'dates',
    description: 'Date account was closed',
    common_conflicts: ['Open status with close date', 'Missing close date on closed account'],
  },
  {
    code: 'BS-27',
    name: 'Date Last Payment',
    group: 'dates',
    description: 'Date of last payment received',
    common_conflicts: ['Payment after charge-off', 'Date conflicts with timeline'],
  },
];

// Field Mapping Item
const FieldMappingItem = ({ mapping, expanded, onToggle, hasConflict }) => {
  const groupColor = FIELD_GROUP_COLORS[mapping.group] || FIELD_GROUP_COLORS.other;

  return (
    <div className={`border rounded-lg overflow-hidden ${
      hasConflict 
        ? 'border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10' 
        : 'border-stone-200 dark:border-stone-700'
    }`}>
      <button
        className="w-full p-3 flex items-center justify-between text-left bg-white dark:bg-stone-900 hover:bg-stone-50 dark:hover:bg-stone-800"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="font-mono text-xs bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300">
            {mapping.code}
          </Badge>
          <span className="font-medium text-stone-800 dark:text-stone-200">
            {mapping.name}
          </span>
          {hasConflict && (
            <AlertTriangle className="w-4 h-4 text-amber-500" />
          )}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`text-xs ${groupColor}`}>
            {mapping.group.replace(/_/g, ' ')}
          </Badge>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {expanded && (
        <div className="p-3 bg-stone-50 dark:bg-stone-800/50 border-t border-stone-200 dark:border-stone-700">
          {/* Description */}
          <p className="text-sm text-stone-600 dark:text-stone-400 mb-3">
            {mapping.description}
          </p>

          {/* Common Conflicts */}
          {mapping.common_conflicts && mapping.common_conflicts.length > 0 && (
            <div className="mb-3">
              <h5 className="text-xs font-medium text-stone-500 dark:text-stone-400 uppercase mb-2">
                Common Conflicts
              </h5>
              <ul className="space-y-1">
                {mapping.common_conflicts.map((conflict, idx) => (
                  <li key={idx} className="text-sm text-amber-600 dark:text-amber-400 flex items-start gap-2">
                    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    {conflict}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Bureau Values if available */}
          {mapping.bureau_values && (
            <div className="p-2 bg-stone-100 dark:bg-stone-800 rounded">
              <h5 className="text-xs font-medium text-stone-500 dark:text-stone-400 mb-1">Reported Values</h5>
              <div className="flex gap-4 text-sm">
                {Object.entries(mapping.bureau_values).map(([bureau, value]) => (
                  <span key={bureau}>
                    <span className="text-stone-500 dark:text-stone-400">{bureau}: </span>
                    <span className="font-medium text-stone-800 dark:text-stone-200">{value || 'N/A'}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main Metro 2 Field Mapping Summary Component
const Metro2FieldMappingSummary = ({
  fieldMappings = [],
  conflictFields = [],
  issueCategories = [],
  compact = false,
}) => {
  const [expandedItems, setExpandedItems] = React.useState({});
  const [showAll, setShowAll] = React.useState(false);
  const [filterGroup, setFilterGroup] = React.useState('all');

  // Use provided mappings or defaults
  const allMappings = fieldMappings.length > 0 ? fieldMappings : DEFAULT_FIELD_MAPPINGS;

  // Filter mappings
  const filteredMappings = filterGroup === 'all' 
    ? allMappings 
    : allMappings.filter(m => m.group === filterGroup);

  const visibleMappings = showAll ? filteredMappings : filteredMappings.slice(0, compact ? 3 : 5);

  const toggleItem = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Get unique groups
  const groups = [...new Set(allMappings.map(m => m.group))];

  // Check if field has conflict
  const hasConflict = (code) => {
    return conflictFields.some(f => 
      f === code || 
      f.toLowerCase().includes(code.toLowerCase()) ||
      code.toLowerCase().includes(f.toLowerCase())
    );
  };

  return (
    <Card className="border-stone-200 dark:border-stone-700" data-testid="metro2-field-mapping-summary">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2 text-stone-800 dark:text-stone-200">
            <FileCode className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            Metro 2 Field Mappings
          </CardTitle>
          <Badge variant="outline" className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
            {conflictFields.length} conflict{conflictFields.length !== 1 ? 's' : ''}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Introduction */}
        {!compact && (
          <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-sm text-purple-700 dark:text-purple-300">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>
                Metro 2 field codes define standardized data elements for credit reporting. 
                Field conflicts across bureaus or within account logic may indicate reporting errors.
              </p>
            </div>
          </div>
        )}

        {/* Group Filter */}
        <div className="flex flex-wrap gap-2">
          <Badge
            variant="outline"
            className={`cursor-pointer ${filterGroup === 'all' ? 'bg-stone-200 dark:bg-stone-700' : ''}`}
            onClick={() => setFilterGroup('all')}
          >
            All
          </Badge>
          {groups.map(group => (
            <Badge
              key={group}
              variant="outline"
              className={`cursor-pointer ${FIELD_GROUP_COLORS[group]} ${filterGroup === group ? 'ring-2 ring-offset-1' : ''}`}
              onClick={() => setFilterGroup(group)}
            >
              {group.replace(/_/g, ' ')}
            </Badge>
          ))}
        </div>

        {/* Field Mapping List */}
        <div className="space-y-2">
          {visibleMappings.map((mapping, index) => (
            <FieldMappingItem
              key={mapping.code}
              mapping={mapping}
              expanded={expandedItems[index]}
              onToggle={() => toggleItem(index)}
              hasConflict={hasConflict(mapping.code)}
            />
          ))}
        </div>

        {/* Show More/Less */}
        {filteredMappings.length > (compact ? 3 : 5) && (
          <Button
            variant="ghost"
            className="w-full text-stone-600 dark:text-stone-400"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? (
              <>Show Less <ChevronUp className="w-4 h-4 ml-1" /></>
            ) : (
              <>Show {filteredMappings.length - visibleMappings.length} More <ChevronDown className="w-4 h-4 ml-1" /></>
            )}
          </Button>
        )}

        {/* Conflict Summary */}
        {conflictFields.length > 0 && (
          <div className="pt-3 border-t border-stone-200 dark:border-stone-700">
            <h4 className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Fields with Conflicts
            </h4>
            <div className="flex flex-wrap gap-2">
              {conflictFields.map((field, idx) => (
                <Badge 
                  key={idx} 
                  variant="outline" 
                  className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-700"
                >
                  {field}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Metro2FieldMappingSummary;
export { FieldMappingItem, DEFAULT_FIELD_MAPPINGS, FIELD_GROUP_COLORS };
